import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Trash2, Truck, X, Plus, AlertCircle } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import { getAllSuppliers, createSupplier, deleteSupplier } from '../utils/supplierApi';
import { ApiValidationError } from '../types/auth';
import type { Supplier, CreateSupplierData } from '../types/supplier';
import type { PaymentTerms } from '../types/customer';
import { toast, toastApiError } from '../utils/toast';

const PAYMENT_TERMS: PaymentTerms[] = ['NET_30', 'NET_60', 'NET_90', 'IMMEDIATE'];

interface FieldErrors {
  [key: string]: string[] | undefined;
}

const formatLabel = (value: string) =>
  value
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const canCreateSupplier = () => {
  const role = localStorage.getItem('user_role')?.toUpperCase();
  return role === 'ADMIN' || role === 'ACCOUNTANT';
};

const canDeleteSupplier = () => localStorage.getItem('user_role')?.toUpperCase() === 'ADMIN';

const defaultFormData = (): CreateSupplierData => ({
  display_name: '',
  email: '',
  phone: '',
  address: '',
  payment_terms: 'NET_30',
  active: true,
});

const SuppliersManage = () => {
  const navigate = useNavigate();
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddSupplierModal, setShowAddSupplierModal] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formData, setFormData] = useState<CreateSupplierData>(defaultFormData());

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!localStorage.getItem('token')) {
        setError('Authentication required. Please sign in again.');
        return;
      }

      const suppliersData = await getAllSuppliers();
      setSuppliers(suppliersData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch vendors');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData(defaultFormData());
    setFieldErrors({});
    setModalError(null);
  };

  const handleCreateSupplier = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setModalError(null);
    setFieldErrors({});

    if (!formData.display_name.trim()) {
      setFieldErrors({ display_name: ['Display name is required'] });
      return;
    }

    try {
      setSubmitting(true);
      await createSupplier({
        display_name: formData.display_name.trim(),
        email: formData.email?.trim() || null,
        phone: formData.phone?.trim() || null,
        address: formData.address?.trim() || null,
        payment_terms: formData.payment_terms,
        active: formData.active,
      });
      setShowAddSupplierModal(false);
      resetForm();
      await fetchData();
      toast.success('Vendor created successfully!');
    } catch (err) {
      if (err instanceof ApiValidationError) {
        setFieldErrors(err.errors);
        setModalError(err.message);
      } else {
        toastApiError(err, 'Failed to create vendor');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteSupplier = async (supplierId: number) => {
    if (!canDeleteSupplier()) {
      toast.error('Only administrators can delete vendors.');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this vendor?')) {
      return;
    }

    try {
      await deleteSupplier(supplierId);
      await fetchData();
      toast.success('Vendor deleted successfully!');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete vendor');
    }
  };

  const filteredSuppliers = suppliers.filter(
    (supplier) =>
      supplier.display_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      supplier.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      supplier.phone?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const fieldError = (field: string) => fieldErrors[field]?.[0];

  if (loading) {
    return (
      <div className="flex h-screen">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-slate-800" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />

      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="bg-white shadow border-b border-gray-200">
          <div className="px-6 py-5">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Supplier Management</h1>
                <p className="mt-1 text-sm text-gray-500">Manage your accounts payable vendors</p>
              </div>

              {canCreateSupplier() && (
                <button
                  onClick={() => {
                    setShowAddSupplierModal(true);
                    setModalError(null);
                    setFieldErrors({});
                  }}
                  className="inline-flex items-center px-5 py-2.5 text-sm font-semibold rounded-lg text-white bg-slate-800 hover:bg-slate-700"
                >
                  <Plus className="h-5 w-5 mr-2" />
                  Add New Supplier
                </button>
              )}
            </div>

            <div className="mt-4">
              <button
                onClick={() => navigate('/suppliers')}
                className="inline-flex items-center px-3 py-2 text-xs font-medium rounded-md text-slate-700 bg-slate-50 hover:bg-slate-100"
              >
                <Truck className="h-3.5 w-3.5 mr-1.5" />
                View All Suppliers
              </button>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-auto p-6">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md flex items-start justify-between gap-4">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-4 w-4 text-red-600 mt-0.5" />
                <p className="text-red-800 text-sm">{error}</p>
              </div>
              <div className="flex gap-2 shrink-0">
                <button onClick={fetchData} className="px-3 py-1 text-sm bg-red-100 text-red-800 rounded hover:bg-red-200">
                  Retry
                </button>
                {error.includes('Authentication') && (
                  <button onClick={() => navigate('/signin')} className="px-3 py-1 text-sm bg-slate-100 text-slate-800 rounded">
                    Sign In
                  </button>
                )}
              </div>
            </div>
          )}

          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-5 border-b border-gray-200 flex justify-between items-center">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Vendors</h2>
                <p className="mt-1 text-sm text-gray-500">Sorted by display name</p>
              </div>

              <div className="w-64">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <input
                    type="text"
                    placeholder="Search vendors..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-slate-800 focus:border-slate-600"
                  />
                </div>
              </div>
            </div>

            <div className="px-6 py-5 overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase">Vendor</th>
                    <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase">Contact</th>
                    <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase">Payment Terms</th>
                    <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase">Balance</th>
                    <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
                    {canDeleteSupplier() && (
                      <th className="px-6 py-3.5 text-right text-xs font-semibold text-gray-500 uppercase">Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredSuppliers.map((supplier) => (
                    <tr key={supplier.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-full bg-slate-50 flex items-center justify-center">
                            <Truck className="h-5 w-5 text-slate-700" />
                          </div>
                          <div>
                            <div className="text-sm font-semibold text-gray-900">{supplier.display_name}</div>
                            {supplier.address && (
                              <div className="text-xs text-gray-500 truncate max-w-xs">{supplier.address}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <div className="text-gray-900">{supplier.email || '—'}</div>
                        <div className="text-xs text-gray-500">{supplier.phone || '—'}</div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {formatLabel(supplier.payment_terms)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {parseFloat(supplier.balance).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex px-2.5 py-1 text-xs font-medium rounded-full ${
                            supplier.active ? 'bg-green-50 text-green-800' : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {supplier.active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      {canDeleteSupplier() && (
                        <td className="px-6 py-4 text-right">
                          <button
                            onClick={() => handleDeleteSupplier(supplier.id)}
                            className="text-gray-400 hover:text-red-600"
                            title="Delete vendor"
                          >
                            <Trash2 className="h-5 w-5" />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>

              {filteredSuppliers.length === 0 && (
                <div className="text-center py-16">
                  <Truck className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-3 text-sm font-medium text-gray-900">No vendors found</h3>
                  <p className="mt-2 text-sm text-gray-500">
                    {searchQuery ? 'Try adjusting your search.' : 'Get started by adding a new vendor.'}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {showAddSupplierModal && (
        <div className="fixed inset-0 bg-gray-800/75 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl">
            <div className="px-6 py-4 border-b flex justify-between items-center bg-gray-50 rounded-t-lg">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Add New Supplier</h3>
                <p className="text-sm text-gray-500">Create a vendor in your company</p>
              </div>
              <button
                onClick={() => {
                  setShowAddSupplierModal(false);
                  resetForm();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleCreateSupplier}>
              <div className="p-6 space-y-5">
                {modalError && (
                  <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg flex gap-2">
                    <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                    <span>{modalError}</span>
                  </div>
                )}

                <div>
                  <label htmlFor="display_name" className="block text-sm font-medium text-gray-700 mb-1">
                    Display Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="display_name"
                    type="text"
                    required
                    maxLength={200}
                    value={formData.display_name}
                    onChange={(e) => setFormData((prev) => ({ ...prev, display_name: e.target.value }))}
                    className={`w-full border rounded-lg px-3.5 py-2.5 text-sm ${
                      fieldError('display_name') ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Acme Suppliers"
                  />
                  {fieldError('display_name') && (
                    <p className="mt-1 text-sm text-red-600">{fieldError('display_name')}</p>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      id="email"
                      type="email"
                      maxLength={150}
                      value={formData.email ?? ''}
                      onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                      className={`w-full border rounded-lg px-3.5 py-2.5 text-sm ${
                        fieldError('email') ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="orders@acme.com"
                    />
                    {fieldError('email') && <p className="mt-1 text-sm text-red-600">{fieldError('email')}</p>}
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                    <input
                      id="phone"
                      type="tel"
                      maxLength={30}
                      value={formData.phone ?? ''}
                      onChange={(e) => setFormData((prev) => ({ ...prev, phone: e.target.value }))}
                      className={`w-full border rounded-lg px-3.5 py-2.5 text-sm ${
                        fieldError('phone') ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="+94771234567"
                    />
                    {fieldError('phone') && <p className="mt-1 text-sm text-red-600">{fieldError('phone')}</p>}
                  </div>
                </div>

                <div>
                  <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                  <textarea
                    id="address"
                    rows={2}
                    value={formData.address ?? ''}
                    onChange={(e) => setFormData((prev) => ({ ...prev, address: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3.5 py-2.5 text-sm"
                    placeholder="456 Industrial Rd"
                  />
                </div>

                <div>
                  <label htmlFor="payment_terms" className="block text-sm font-medium text-gray-700 mb-1">
                    Payment Terms
                  </label>
                  <select
                    id="payment_terms"
                    value={formData.payment_terms}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        payment_terms: e.target.value as PaymentTerms,
                      }))
                    }
                    className="w-full border border-gray-300 rounded-lg px-3.5 py-2.5 text-sm bg-white"
                  >
                    {PAYMENT_TERMS.map((term) => (
                      <option key={term} value={term}>
                        {formatLabel(term)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex items-center">
                  <input
                    id="active"
                    type="checkbox"
                    checked={formData.active ?? true}
                    onChange={(e) => setFormData((prev) => ({ ...prev, active: e.target.checked }))}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  <label htmlFor="active" className="ml-2.5 text-sm text-gray-700">
                    Active vendor
                  </label>
                </div>
              </div>

              <div className="px-6 py-4 bg-gray-50 border-t flex justify-end gap-3 rounded-b-lg">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddSupplierModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 bg-white"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 rounded-md text-sm font-medium text-white bg-slate-800 hover:bg-slate-700 disabled:opacity-60"
                >
                  {submitting ? 'Creating...' : 'Create Supplier'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default SuppliersManage;
